/**
 * Day-of venue (public self) check-in on / off for Regional and Territory Managers (owner decision 2026-10-29:
 * running the event, not configuring it). Everyone else uses the control in the Tickets tab's ticketing panel, which
 * is view-only for RM/TM; this exposes only that one switch to them, on the Operations tab. Server:
 * set_event_checkin_enabled (mig 1241: RM/TM on any non-deleted event they can run).
 */
import React, { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import Button from "../common/Button";
import { useToast } from "../ui/Toast";
import { setEventCheckinEnabled } from "../../services/eventTicketing.service";
import { eventKeys } from "../../hooks/useEvents";

interface Props {
  eventId: string;
  /** events.self_checkin_disabled as loaded (true = closed; false or null = open, as the server treats it) */
  disabled: boolean;
}

const VenueCheckinToggle: React.FC<Props> = ({ eventId, disabled }) => {
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const [closed, setClosed] = useState(disabled);
  const [busy, setBusy] = useState(false);

  const set = async (open: boolean) => {
    setBusy(true);
    try {
      await setEventCheckinEnabled(eventId, open);
      setClosed(!open);
      queryClient.invalidateQueries({ queryKey: eventKeys.detail(eventId) });
      showToast(
        "success",
        open ? "Venue check-in is now open." : "Venue check-in closed.",
      );
    } catch (e) {
      showToast(
        "error",
        (e as Error).message || "Couldn't change venue check-in.",
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className={`mb-4 flex flex-wrap items-center justify-between gap-2 rounded-md border p-3 text-sm ${
        closed
          ? "border-amber-200 bg-amber-50 text-amber-900"
          : "border-green-200 bg-green-50 text-green-800"
      }`}
      data-testid="venue-checkin-toggle"
    >
      <span>
        Venue self check-in is <strong>{closed ? "closed" : "open"}</strong>.
      </span>
      <Button
        size="sm"
        variant={closed ? "primary" : "secondary"}
        onClick={() => set(closed)}
        disabled={busy}
        isLoading={busy}
        data-testid="venue-checkin-toggle-button"
      >
        {closed ? "Open venue check-in" : "Close venue check-in"}
      </Button>
    </div>
  );
};

export default VenueCheckinToggle;
